import { inject, Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { Directionality } from '@angular/cdk/bidi';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { LangCode } from '../../models';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  currentLanguage: LangCode;
  currentDirection: 'rtl' | 'ltr';

  private dirService = inject(Directionality);
  private translate = inject(TranslateService);
  
  private languageChanged$ = new BehaviorSubject<LangCode>(LangCode.English);
  public languageChange$ = this.languageChanged$.asObservable();
  
  // Add a change counter to help pipes detect language changes
  private changeCounter = 0;
  private readonly syncLanguageFromBrowserState = () => {
    void this.syncLanguageWithCurrentContext(true);
  };

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject('REQUEST') private request?: any
  ) {
    this.translate.addLangs([LangCode.English, LangCode.Arabic]);
    this.translate.setDefaultLang(LangCode.English); // Fix: Start with English to prevent iOS crashes
    const initialLanguage = this.resolvePreferredLanguage();
    this.currentLanguage = initialLanguage;
    this.currentDirection = this.getDirection(initialLanguage);

    // Initialize language only on browser
    if (isPlatformBrowser(this.platformId)) {
      // iOS Safari fix: Delay language detection to prevent crashes
      if (this.isIOSSafari()) {
        setTimeout(() => {
          void this.syncLanguageWithCurrentContext(false);
        }, 100);
      } else {
        void this.syncLanguageWithCurrentContext(false);
      }

      window.addEventListener('pageshow', this.syncLanguageFromBrowserState);
      window.addEventListener('popstate', this.syncLanguageFromBrowserState);
    } else {
      this.currentLanguage = initialLanguage;
      this.currentDirection = this.getDirection(initialLanguage);
    }
  }

  private isIOSSafari(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && 
           /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  private initializeLanguage(): void {
    void this.syncLanguageWithCurrentContext(false);
  }

  switchLanguage(lang: LangCode) {
    void this.ensureLanguageLoaded(lang, { persist: true, emit: true });
    return true;
  }

 
  /**
   * Clean URL parameters to prevent iOS Safari routing conflicts
   */
  private cleanUrlParameters() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const url = new URL(window.location.href);
    if (url.searchParams.has('language')) {
      url.searchParams.delete('language');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }
  getCurrentLanguage(): LangCode {
    const resolvedLanguage = this.resolvePreferredLanguage();
    this.currentLanguage = resolvedLanguage;
    this.currentDirection = this.getDirection(resolvedLanguage);
    return resolvedLanguage;
  }
  getChangeCounter(): number {
    return this.changeCounter;
  }

  async ensureLanguageLoaded(
    lang: LangCode,
    options: { persist?: boolean; emit?: boolean } = {}
  ): Promise<void> {
    const persist = options.persist ?? true;
    const emit = options.emit ?? true;
    const previousLanguage = this.currentLanguage;
    const previousDirection = this.currentDirection;
    const nextDirection = this.getDirection(lang);

    this.currentLanguage = lang;
    this.currentDirection = nextDirection;

    if (persist && isPlatformBrowser(this.platformId)) {
      localStorage.setItem('app_language', lang);
      document.cookie = `app_language=${lang}; path=/; max-age=31536000`;
    }

    const shouldEmit = emit && (previousLanguage !== lang || previousDirection !== nextDirection);
    if (shouldEmit) {
      this.dirService.change.next(nextDirection);
      this.languageChanged$.next(lang);
      this.changeCounter++;
    }

    if (this.translate.currentLang === lang) {
      return;
    }

    await firstValueFrom(
      this.translate.use(lang).pipe(
        map(() => void 0),
        catchError((err) => {
          console.warn('Translation loading failed, using fallback:', err);
          return of(void 0);
        })
      )
    );

    if (emit) {
      this.dirService.change.next(nextDirection);
    }
  }

  private async syncLanguageWithCurrentContext(emit: boolean): Promise<void> {
    const resolvedLanguage = this.resolvePreferredLanguage();
    const needsSync =
      this.currentLanguage !== resolvedLanguage || this.translate.currentLang !== resolvedLanguage;

    if (needsSync) {
      await this.ensureLanguageLoaded(resolvedLanguage, {
        persist: isPlatformBrowser(this.platformId),
        emit
      });
      return;
    }

    this.currentLanguage = resolvedLanguage;
    this.currentDirection = this.getDirection(resolvedLanguage);
  }

  private resolvePreferredLanguage(): LangCode {
    if (isPlatformBrowser(this.platformId)) {
      const queryLang = this.getValidLanguage(window.location.search ? new URLSearchParams(window.location.search).get('language') : null);
      if (queryLang) {
        return queryLang;
      }

      const pathLang = this.getLanguageFromPath(window.location.pathname);
      if (pathLang) {
        return pathLang;
      }

      const savedLang = this.getValidLanguage(localStorage.getItem('app_language'));
      if (savedLang) {
        return savedLang;
      }

      const browserLang = this.getValidLanguage(this.translate.getBrowserLang());
      return browserLang ?? LangCode.English;
    }

    const serverPathLang = this.getLanguageFromServerRequest();
    if (serverPathLang) {
      return serverPathLang;
    }

    const cookieLang = this.getLanguageFromCookie(this.request?.headers?.cookie as string | undefined);
    return cookieLang ?? LangCode.English;
  }

  private getLanguageFromServerRequest(): LangCode | null {
    if (!this.request?.url) {
      return null;
    }

    try {
      const urlPath = new URL(this.request.url, 'http://localhost').pathname;
      return this.getLanguageFromPath(urlPath);
    } catch {
      return this.getLanguageFromPath(String(this.request.url));
    }
  }

  private getLanguageFromPath(path: string): LangCode | null {
    const firstSegment = path.split('/').filter(Boolean)[0] ?? null;
    return this.getValidLanguage(firstSegment);
  }

  private getLanguageFromCookie(cookieHeader?: string): LangCode | null {
    if (!cookieHeader) {
      return null;
    }

    const match = cookieHeader.match(/(?:^|;\s*)app_language=(en|ar)(?:;|$)/);
    return this.getValidLanguage(match?.[1] ?? null);
  }

  private getValidLanguage(value: string | null | undefined): LangCode | null {
    if (value === LangCode.English || value === LangCode.Arabic) {
      return value;
    }
    return null;
  }

  private getDirection(lang: LangCode): 'rtl' | 'ltr' {
    return lang === LangCode.Arabic ? 'rtl' : 'ltr';
  }
}
